import React, { useEffect, useRef, useState } from "react";
import { PaymentRequestButtonElement } from "@stripe/react-stripe-js";

import http from "../../../helpers/http";

const COUNTRY = "US";
const CURRENCY = "usd";

export const getApplePayTotalCents = ({ plan, appliedCoupon }) => {
  const total =
    appliedCoupon?.total_after_discount != null
      ? appliedCoupon.total_after_discount
      : plan?.price;
  const amount = Number(total);

  if (!Number.isFinite(amount) || amount <= 0) {
    return 0;
  }

  return Math.round(amount * 100);
};

export const createApplePayPaymentIntent = async (
  paymentMethodId,
  postData,
  planId
) => {
  const backendFormData = new FormData();
  Object.keys(postData).forEach((key) => {
    backendFormData.append(key, postData[key]);
  });
  backendFormData.append("payment_method", paymentMethodId);
  backendFormData.append("payment_method_type", "card");
  backendFormData.append("wallet_payment", "apple_pay");
  backendFormData.append("plan_id", planId);
  backendFormData.append("token", localStorage.getItem("authToken"));
  backendFormData.append("host", window.location.hostname);

  const { data } = await http.post("/create-payment-intent", backendFormData);
  return data;
};

const ApplePayButton = ({
  stripe,
  plan,
  planId,
  postData,
  appliedCoupon,
  disabled = false,
  validateBeforePayment,
  createPaymentIntent,
  saveSubscription,
  onStart,
  onFinish,
  onError,
  onCouponError,
  isCouponError,
  formatCouponMessage,
  onAvailabilityChange,
}) => {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const amount = getApplePayTotalCents({ plan, appliedCoupon });
  const planLabel = plan?.plan_name || "Rhino Recon subscription";
  const latestProps = useRef({});

  latestProps.current = {
    postData,
    planId,
    disabled,
    validateBeforePayment,
    createPaymentIntent,
    saveSubscription,
    onStart,
    onFinish,
    onError,
    onCouponError,
    isCouponError,
    formatCouponMessage,
    onAvailabilityChange,
  };

  useEffect(() => {
    const notifyAvailability = (value) => {
      latestProps.current.onAvailabilityChange?.(value);
    };

    if (!stripe || amount <= 0) {
      setPaymentRequest(null);
      notifyAvailability(false);
      return undefined;
    }

    let isMounted = true;
    setPaymentRequest(null);
    notifyAvailability(null);
    const request = stripe.paymentRequest({
      country: COUNTRY,
      currency: CURRENCY,
      total: {
        label: planLabel,
        amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    request.on("paymentmethod", async (event) => {
      const current = latestProps.current;

      if (current.disabled) {
        event.complete("fail");
        return;
      }

      if (
        current.validateBeforePayment &&
        !current.validateBeforePayment()
      ) {
        event.complete("fail");
        return;
      }

      current.onStart?.();

      const paymentMethodId = event.paymentMethod?.id;
      if (!paymentMethodId) {
        current.onError?.("Apple Pay did not return a payment method.");
        event.complete("fail");
        current.onFinish?.();
        return;
      }

      try {
        const walletPostData = {
          ...current.postData,
          name_on_card:
            current.postData.name_on_card ||
            event.payerName ||
            "Apple Pay Customer",
        };
        const createIntent =
          current.createPaymentIntent ||
          ((id, data) =>
            createApplePayPaymentIntent(id, data, current.planId));
        const responseData = await createIntent(paymentMethodId, walletPostData);

        if (responseData?.status !== 1) {
          if (current.isCouponError?.(responseData)) {
            current.onCouponError?.(
              current.formatCouponMessage
                ? current.formatCouponMessage(responseData.msg)
                : responseData.msg
            );
          } else {
            current.onError?.(
              responseData?.msg || "Apple Pay checkout could not be started."
            );
          }
          event.complete("fail");
          return;
        }

        const saveResult = await current.saveSubscription?.({
          ...walletPostData,
          subscriptionId: responseData.subscriptionId,
          customer_id: responseData.customerId,
          plan_id: current.planId,
          paymentIntent: responseData.paymentIntentId || "",
          payment_method_id: paymentMethodId,
          payment_method_type: "card",
          wallet_payment: "apple_pay",
          effective_price: responseData.effective_price,
          coupon_db_id: responseData.coupon_db_id,
          host: window.location.hostname,
        });

        if (!saveResult?.status) {
          current.onError?.(
            saveResult?.msg || "Apple Pay payment could not be verified."
          );
          event.complete("fail");
          return;
        }
        event.complete("success");
      } catch (error) {
        current.onError?.(error.message || "Unable to process Apple Pay.");
        event.complete("fail");
      } finally {
        current.onFinish?.();
      }
    });

    request.canMakePayment()
      .then((result) => {
        if (isMounted && result?.applePay) {
          setPaymentRequest(request);
          notifyAvailability(true);
        } else if (isMounted) {
          setPaymentRequest(null);
          notifyAvailability(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setPaymentRequest(null);
          notifyAvailability(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    amount,
    planLabel,
    stripe,
  ]);

  if (!paymentRequest) {
    return null;
  }

  return (
    <div
      className="blk_form"
      aria-disabled={disabled}
      style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
    >
      <PaymentRequestButtonElement
        key={amount}
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: "subscribe",
              theme: "dark",
              height: "48px",
            },
          },
        }}
      />
    </div>
  );
};

export default ApplePayButton;
